import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Zap, ArrowRight, Sparkles, CalendarCheck, Mail, Compass } from "lucide-react";
import AuthHero from "@/components/AuthHero";

export default function Login() {
  const [email, setEmail] = useState("demo@autoclose.ai");
  const [password, setPassword] = useState("demo1234");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/deals");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] bg-background">
      <div className="flex flex-col p-8 lg:p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-md shadow-[0_2px_12px_hsl(var(--primary)/0.45)]"><Zap className="w-4 h-4 text-primary-foreground" fill="currentColor" /></div>
          <span className="display font-bold tracking-tight text-lg">Auto-Close<span className="text-primary">-Ai</span></span>
        </div>
        <div className="flex-1 flex items-center">
          <form onSubmit={submit} className="w-full max-w-sm space-y-6 stagger">
            <div>
              <div className="eyebrow mb-3">Welcome back</div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95]">Sign in.</h1>
              <p className="text-sm text-muted-foreground mt-3">Your pipeline, outbound and inbox — one surface, on autopilot.</p>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="h-10" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="h-10" />
            </div>
            <Button data-testid="login-submit" disabled={loading} className="w-full h-10 text-sm group">{loading ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" /></>}</Button>
            <div className="text-sm text-muted-foreground">
              New here? <Link to="/register" className="text-primary font-medium hover:underline underline-offset-4" data-testid="link-register">Create a workspace</Link>
            </div>
            <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
              <span>Demo account</span><span className="mono text-foreground/80">demo@autoclose.ai / demo1234</span>
            </div>
          </form>
        </div>
        <div className="text-xs text-muted-foreground mono">Autonomous AI sales platform + CRM</div>
      </div>
      <AuthHero
        headline={<>Prospects found. Emails written. <span className="text-white/60">Meetings booked.</span></>}
        points={[
          { icon: Compass, text: "Turn website visitors into booked buyer consults automatically" },
          { icon: Sparkles, text: "Claude-personalized sequences with human approval when you want it" },
          { icon: Mail, text: "Reply detection, deliverability health and inbox warmup built in" },
          { icon: CalendarCheck, text: "Meetings land on your calendar — deals advance automatically" },
        ]}
      />
    </div>
  );
}
